import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Heart, Calendar, FileText, Image as ImageIcon, Video, Filter, Play } from 'lucide-react';
import { useGetUserSponsorshipUpdatesQuery, useGetUserChildDetailsQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Navbar from '../../components/common/Navbar';

const SponsorshipUpdates: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const childId = id ? parseInt(id) : null;
  const isValidId = childId && !isNaN(childId) && childId > 0;

  const [currentPage, setCurrentPage] = useState(1);
  const [updateTypeFilter, setUpdateTypeFilter] = useState<'all' | 'progress_report' | 'photo_update' | 'video_update' | 'general_update'>('all');
  const itemsPerPage = 10;

  const { data: childResponse } = useGetUserChildDetailsQuery(childId!, {
    skip: !isValidId,
  });

  const { data: updatesResponse, isLoading } = useGetUserSponsorshipUpdatesQuery({
    child_id: childId!,
    page: currentPage,
    limit: itemsPerPage,
    update_type: updateTypeFilter === 'all' ? undefined : updateTypeFilter,
  }, {
    skip: !isValidId || !user,
  });

  const child = childResponse?.data;
  const updates = updatesResponse?.data?.updates || [];
  const pagination = updatesResponse?.data?.pagination || {
    current_page: 1,
    total_pages: 1,
    total_updates: 0,
    limit: itemsPerPage,
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

  const getUpdateTypeBadge = (type: string) => {
    switch (type) {
      case 'progress_report':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Progress Report
          </Badge>
        );
      case 'photo_update':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            Photo Update
          </Badge>
        );
      case 'video_update':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Video className="w-3 h-3" />
            Video Update
          </Badge>
        );
      case 'general_update':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            General Update
          </Badge>
        );
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          {child && (
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                <img
                  src={child.profile_image || 'https://via.placeholder.com/64?text=No+Image'}
                  alt={child.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1">{child.name}'s Updates</h1>
                <p className="text-gray-600">{child.location} • {child.education_stage}</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by Type:</span>
            {(['all', 'progress_report', 'photo_update', 'video_update', 'general_update'] as const).map((type) => (
              <button
                key={type}
                onClick={() => {
                  setUpdateTypeFilter(type);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  updateTypeFilter === type
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type === 'progress_report' ? 'Progress Report' :
                 type === 'photo_update' ? 'Photo Update' :
                 type === 'video_update' ? 'Video Update' :
                 type === 'general_update' ? 'General Update' :
                 'All'}
              </button>
            ))}
          </div>
        </Card>

        {/* Updates List */}
        {updates.length === 0 ? (
          <Card className="p-12 text-center">
            <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Updates Yet</h3>
            <p className="text-gray-600">
              {updateTypeFilter === 'all'
                ? "There are no updates available for this child yet. Updates will appear here when the admin posts them."
                : `No ${updateTypeFilter} updates found.`}
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {updates.map((update) => (
              <Card key={update.update_id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {getUpdateTypeBadge(update.update_type)}
                      <span className="text-sm text-gray-600">
                        {formatDate(update.update_date)}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{update.title}</h3>
                    {update.created_by && (
                      <p className="text-sm text-gray-600">Posted by {update.created_by}</p>
                    )}
                  </div>
                </div>

                {update.description && (
                  <p className="text-gray-700 mb-4 leading-relaxed">{update.description}</p>
                )}

                {update.content && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{update.content}</p>
                  </div>
                )}

                {/* Images */}
                {update.images && update.images.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Photos ({update.images.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {update.images.map((image, index) => (
                        <div key={index} className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden group">
                          <img
                            src={image}
                            alt={`Update image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300?text=Image+Error';
                            }}
                          />
                          <a
                            href={image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ImageIcon className="w-8 h-8 text-white" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {update.videos && update.videos.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Videos ({update.videos.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {update.videos.map((video, index) => (
                        <div key={index} className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
                          <video
                            src={video}
                            controls
                            className="w-full h-full"
                            onError={(e) => {
                              console.error('Video load error:', e);
                            }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default SponsorshipUpdates;
